import { ThemingContext, Icon, IconSize, IconColor } from '@doordash/design-language';
import { IconButtonIconType, SizeSemantics } from './constants.js';
import { ButtonRoot, SpinnerContainer } from './styles.js';
import { IconButtonSize, IconButtonState, IconButtonType } from './types.js';
import { generateRoleRelatedProps, loadingSizeForButton, loadingStateForButton, iconSizeForIconButton } from './utils.js';
import Loading from '@doordash/component-loading';
import React from 'react';
import { getPropsToForward } from '@doordash/utilities';
import invariant from 'invariant';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
// to properly set `tagProps` to accept any HTML `button` props. If a different `tag` prop
// is provided, TS will be able to infer that and set `tagProps` to the custom `tag` element's props

var defaultElement = 'button';

/**
 * Button component that only renders an icon.
 * @see {@link https://prism.doordash.com/components/button | Prism Web Documentation: IconButton}
 * @see {@link https://s3-design-language-system.cdn4dd.com/master/index.html?path=/story/controls-component-button-iconbutton--playground | Prism Web Storybook: IconButton}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role | Accessibility Guidelines}
 * @public
 * @component
 * @componentAPI {@link IconButtonProps}
 * @componentConstant {@link IconButtonSize}
 * @componentConstant {@link IconButtonState}
 * @componentConstant {@link IconButtonType}
 * @componentConstant {@link IconButtonIconType}
 * @componentSemantic Button
 */
var IconButton = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$type = _ref.type,
      type = _ref$type === void 0 ? IconButtonType.Plain : _ref$type,
      iconType = _ref.iconType,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? IconButtonSize.Medium : _ref$size,
      onClick = _ref.onClick,
      _ref$isDisabled = _ref.isDisabled,
      isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled,
      _ref$isToggleable = _ref.isToggleable,
      isToggleable = _ref$isToggleable === void 0 ? false : _ref$isToggleable,
      _ref$isToggled = _ref.isToggled,
      isToggled = _ref$isToggled === void 0 ? false : _ref$isToggled,
      _ref$state = _ref.state,
      state = _ref$state === void 0 ? IconButtonState.Default : _ref$state,
      id = _ref.id,
      _ref$tag = _ref.tag,
      tag = _ref$tag === void 0 ? defaultElement : _ref$tag,
      _ref$isDepressed = _ref.isDepressed,
      isDepressed = _ref$isDepressed === void 0 ? false : _ref$isDepressed,
      href = _ref.href,
      target = _ref.target,
      accessibilityLabel = _ref.accessibilityLabel,
      accessibilityDescribedBy = _ref.accessibilityDescribedBy,
      accessibilityRole = _ref.accessibilityRole,
      onAnimationEnd = _ref.onAnimationEnd,
      _ref$isRaised = _ref.isRaised,
      isRaised = _ref$isRaised === void 0 ? false : _ref$isRaised,
      tagProps = _ref.tagProps,
      props = _objectWithoutProperties(_ref, ["type", "iconType", "size", "onClick", "isDisabled", "isToggleable", "isToggled", "state", "id", "tag", "isDepressed", "href", "target", "accessibilityLabel", "accessibilityDescribedBy", "accessibilityRole", "onAnimationEnd", "isRaised", "tagProps"]);

  var theme = React.useContext(ThemingContext);
  invariant(isRaised ? type === IconButtonType.Plain || type === IconButtonType.Primary || type === IconButtonType.PrimaryPill : true, "\n      You can only use isRaised with Plain, Primary\n      or PrimaryPill type buttons.\n    ");
  invariant(type === IconButtonType.FlatPrimary || type === IconButtonType.FlatSecondary ? !isToggleable : true, "\n      Do not set isToggleable to true when type is\n      IconButton.Types.FlatPrimary or IconButton.Types.FlatSecondary.\n    ");
  invariant(typeof accessibilityLabel !== 'undefined' && typeof accessibilityLabel === 'string' && accessibilityLabel.length > 1, "\n      You must provide an accessibilityLabel to an IconButton\n      to indicate what should be used by assistive technology.\n    ");
  React.useEffect(function () {
    if (size === IconButtonSize.XLarge) {
      // eslint-disable-next-line no-console
      console.warn('Warning! `XLarge` IconButton Size will be removed in Prism Web v8 - we recommend changing your size to `Large`');
    }
  }, [size]);
  var propsToForward = getPropsToForward({
    props: props,
    prefixesToForward: ['data-', 'aria-']
  });
  return /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    themeColors: theme.Colors,
    themeBorderRadius: theme.BorderRadius.ButtonDefault,
    themeHeight: theme.Size[SizeSemantics[size]],
    id: id,
    isIconButton: true,
    isInline: true,
    size: size,
    isRaised: isRaised,
    shape: theme.Shape.IconButtonDefault,
    isDepressed: isDepressed,
    isToggleable: isToggleable,
    isToggled: isToggled,
    kind: type,
    target: target,
    "aria-label": accessibilityLabel,
    "aria-describedby": accessibilityDescribedBy,
    "aria-pressed": isToggleable ? isToggled : undefined,
    ref: ref
  }, propsToForward, generateRoleRelatedProps({
    isDisabled: isDisabled,
    tag: tag,
    href: href,
    state: state,
    onClick: onClick,
    accessibilityRole: accessibilityRole
  }), tagProps), state !== IconButtonState.Default ? /*#__PURE__*/React.createElement(SpinnerContainer, null, /*#__PURE__*/React.createElement(Loading, {
    size: Loading.Sizes[loadingSizeForButton(size)],
    color: Loading.Colors.CurrentColor,
    state: loadingStateForButton(state),
    onAnimationEnd: onAnimationEnd
  })) : /*#__PURE__*/React.createElement(Icon, {
    type: iconType,
    shouldAdjustSmallSizesWhitespace: true,
    size: IconSize[iconSizeForIconButton(size)],
    color: IconColor.CurrentColor
  }));
}); // Quick and dirty way to add statics here
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-894053907

var IconButtonNamespace = Object.assign(IconButton, {
  Sizes: IconButtonSize,
  States: IconButtonState,
  Types: IconButtonType,
  IconTypes: IconButtonIconType
});

export { IconButtonNamespace as default };
